import { useCallback, useState, useEffect, useRef } from 'react';
import { useFilters } from '@/contexts/FilterContext';
import { useUserPermissions } from './useUserPermissions';

// Delay before showing tooltip after filter becomes empty (allows dropdown to close)
const TOOLTIP_SHOW_DELAY = 350;

export interface FilterOnboardingState {
  /** Show tooltip when no industry filter selected (highest priority) */
  showIndustryOnboarding: boolean;
  /** Show tooltip when no company filter selected (if user can see companies) */
  showCompanyOnboarding: boolean;
  /** Show tooltip when no group filter selected (if user can see groups) */
  showGroupOnboarding: boolean;
  /** Show tooltip when models are all deselected but others are selected */
  showModelOnboarding: boolean;
  /** Message for industry onboarding tooltip */
  industryMessage: string;
  /** Message for company onboarding tooltip */
  companyMessage: string;
  /** Message for group onboarding tooltip */
  groupMessage: string;
  /** Message for model onboarding tooltip */
  modelMessage: string;
  /** Dismiss handler for industry tooltip */
  dismissIndustry: () => void;
  /** Dismiss handler for company tooltip */
  dismissCompany: () => void;
  /** Dismiss handler for group tooltip */
  dismissGroup: () => void;
  /** Dismiss handler for model tooltip */
  dismissModel: () => void;
}

export interface UseFilterOnboardingOptions {
  /** When true, content (map/dashboard) has finished loading - enables onboarding tooltips */
  contentLoaded?: boolean;
}

/**
 * Hook to manage filter onboarding state for Map and Dashboard pages.
 *
 * Shows guidance tooltips when filters are empty (priority order):
 * 1. Industry filter (highest priority)
 * 2. Company filter (if user can see companies)
 * 3. Group filter (if user can see groups)
 * 4. Model filter (lowest priority)
 *
 * Only ONE tooltip is shown at a time, based on priority.
 * Tooltips appear EVERY TIME a filter is empty - they are reminders, not one-time education.
 * Manual dismiss only hides for the current page session (resets on reload or navigation).
 * Tooltips appear with a delay to allow dropdown menus to close first.
 * Tooltips only appear after contentLoaded is true.
 */
export function useFilterOnboarding(options: UseFilterOnboardingOptions = {}): FilterOnboardingState {
  const { contentLoaded = false } = options;
  const {
    selectedCompanyIds,
    selectedGroupIds,
    selectedModels,
    selectedIndustryTypes,
    isLoading: filterContextLoading
  } = useFilters();
  const { permissions, loading: permissionsLoading } = useUserPermissions();

  // Manual dismissal state - resets on each page load (no sessionStorage)
  // This lets users temporarily dismiss, but tooltip returns if they come back to empty state
  const [manuallyDismissedIndustry, setManuallyDismissedIndustry] = useState(false);
  const [manuallyDismissedCompany, setManuallyDismissedCompany] = useState(false);
  const [manuallyDismissedGroup, setManuallyDismissedGroup] = useState(false);
  const [manuallyDismissedModel, setManuallyDismissedModel] = useState(false);

  // Delayed show state - prevents tooltip from appearing immediately when clearing selections
  const [delayedShowIndustry, setDelayedShowIndustry] = useState(false);
  const [delayedShowCompany, setDelayedShowCompany] = useState(false);
  const [delayedShowGroup, setDelayedShowGroup] = useState(false);
  const [delayedShowModel, setDelayedShowModel] = useState(false);

  // Timeout refs for cleanup
  const industryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const companyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const groupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const modelTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check which filters user can see
  const canSeeCompanies = permissions.canSeeCompanies;
  const canSeeGroups = permissions.canSeeGroups;

  // Check if each filter has selections
  const hasIndustrySelection = selectedIndustryTypes.length > 0;
  const hasCompanySelection = selectedCompanyIds.length > 0;
  const hasGroupSelection = selectedGroupIds.length > 0;
  const hasModelSelection = selectedModels.length > 0;

  // Wait for context to be ready AND content to be loaded before showing onboarding
  const isReady = !filterContextLoading && !permissionsLoading && contentLoaded;

  // Reset manual dismissal when selection state changes (user makes a selection then clears it)
  // This ensures tooltip comes back when filter becomes empty again
  useEffect(() => {
    if (hasIndustrySelection) {
      setManuallyDismissedIndustry(false);
    }
  }, [hasIndustrySelection]);

  useEffect(() => {
    if (hasCompanySelection) {
      setManuallyDismissedCompany(false);
    }
  }, [hasCompanySelection]);

  useEffect(() => {
    if (hasGroupSelection) {
      setManuallyDismissedGroup(false);
    }
  }, [hasGroupSelection]);

  useEffect(() => {
    if (hasModelSelection) {
      setManuallyDismissedModel(false);
    }
  }, [hasModelSelection]);

  // Compute raw show conditions (priority: industry > company > group > model)
  // Industry: show if no industry selected
  const shouldShowIndustry = isReady && !hasIndustrySelection && !manuallyDismissedIndustry;

  // Company: show if industry selected, no company selected, user can see companies
  const shouldShowCompany = isReady && !shouldShowIndustry && hasIndustrySelection &&
    canSeeCompanies && !hasCompanySelection && !manuallyDismissedCompany;

  // Group: show if industry selected, company selected (or user can't see companies), no group selected, user can see groups
  const shouldShowGroup = isReady && !shouldShowIndustry && !shouldShowCompany &&
    hasIndustrySelection && (hasCompanySelection || !canSeeCompanies) &&
    canSeeGroups && !hasGroupSelection && !manuallyDismissedGroup;

  // Model: show if all other filters have selections
  const shouldShowModel = isReady && !shouldShowIndustry && !shouldShowCompany && !shouldShowGroup &&
    hasIndustrySelection && (hasCompanySelection || !canSeeCompanies) &&
    (hasGroupSelection || !canSeeGroups) && !hasModelSelection && !manuallyDismissedModel;

  // Handle delayed show for Industry tooltip
  useEffect(() => {
    if (industryTimeoutRef.current) {
      clearTimeout(industryTimeoutRef.current);
      industryTimeoutRef.current = null;
    }

    if (shouldShowIndustry) {
      industryTimeoutRef.current = setTimeout(() => {
        setDelayedShowIndustry(true);
      }, TOOLTIP_SHOW_DELAY);
    } else {
      setDelayedShowIndustry(false);
    }

    return () => {
      if (industryTimeoutRef.current) {
        clearTimeout(industryTimeoutRef.current);
      }
    };
  }, [shouldShowIndustry]);

  // Handle delayed show for Company tooltip
  useEffect(() => {
    if (companyTimeoutRef.current) {
      clearTimeout(companyTimeoutRef.current);
      companyTimeoutRef.current = null;
    }

    if (shouldShowCompany) {
      companyTimeoutRef.current = setTimeout(() => {
        setDelayedShowCompany(true);
      }, TOOLTIP_SHOW_DELAY);
    } else {
      setDelayedShowCompany(false);
    }

    return () => {
      if (companyTimeoutRef.current) {
        clearTimeout(companyTimeoutRef.current);
      }
    };
  }, [shouldShowCompany]);

  // Handle delayed show for Group tooltip
  useEffect(() => {
    if (groupTimeoutRef.current) {
      clearTimeout(groupTimeoutRef.current);
      groupTimeoutRef.current = null;
    }

    if (shouldShowGroup) {
      groupTimeoutRef.current = setTimeout(() => {
        setDelayedShowGroup(true);
      }, TOOLTIP_SHOW_DELAY);
    } else {
      setDelayedShowGroup(false);
    }

    return () => {
      if (groupTimeoutRef.current) {
        clearTimeout(groupTimeoutRef.current);
      }
    };
  }, [shouldShowGroup]);

  // Handle delayed show for Model tooltip
  useEffect(() => {
    if (modelTimeoutRef.current) {
      clearTimeout(modelTimeoutRef.current);
      modelTimeoutRef.current = null;
    }

    if (shouldShowModel) {
      modelTimeoutRef.current = setTimeout(() => {
        setDelayedShowModel(true);
      }, TOOLTIP_SHOW_DELAY);
    } else {
      setDelayedShowModel(false);
    }

    return () => {
      if (modelTimeoutRef.current) {
        clearTimeout(modelTimeoutRef.current);
      }
    };
  }, [shouldShowModel]);

  // Final show states - only show after delay
  const showIndustryOnboarding = shouldShowIndustry && delayedShowIndustry;
  const showCompanyOnboarding = shouldShowCompany && delayedShowCompany;
  const showGroupOnboarding = shouldShowGroup && delayedShowGroup;
  const showModelOnboarding = shouldShowModel && delayedShowModel;

  // Manual dismissal handlers - only hide until filter state changes
  const dismissIndustry = useCallback(() => {
    setManuallyDismissedIndustry(true);
  }, []);

  const dismissCompany = useCallback(() => {
    setManuallyDismissedCompany(true);
  }, []);

  const dismissGroup = useCallback(() => {
    setManuallyDismissedGroup(true);
  }, []);

  const dismissModel = useCallback(() => {
    setManuallyDismissedModel(true);
  }, []);

  // Messages for each filter type
  const industryMessage = 'Select an industry to view units';
  const companyMessage = 'Select a company to view units';
  const groupMessage = 'Select a group to view units';
  const modelMessage = 'Select models to display on map';

  return {
    showIndustryOnboarding,
    showCompanyOnboarding,
    showGroupOnboarding,
    showModelOnboarding,
    industryMessage,
    companyMessage,
    groupMessage,
    modelMessage,
    dismissIndustry,
    dismissCompany,
    dismissGroup,
    dismissModel,
  };
}

export default useFilterOnboarding;
